import { useState, useEffect } from "react";
import { fetchFeatureFlags, getCachedFlags } from "./featureFlagsService";
import { FeatureFlagName } from "./types";

export const useFeatureFlags = () => {
  const initialFlags = getCachedFlags() ?? {};
  const [flags, setFlags] = useState<Record<string, boolean>>(initialFlags);
  const [isLoading, setIsLoading] = useState(!getCachedFlags());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const loadFlags = async () => {
      try {
        const fetchedFlags = await fetchFeatureFlags();
        if (mounted) {
          setFlags(fetchedFlags);
          setError(null);
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : "Failed to load feature flags");
          // Fall back to cached flags or empty object
          setFlags(getCachedFlags() ?? {});
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadFlags();

    return () => {
      mounted = false;
    };
  }, []);

  return { flags, isLoading, error };
};

export const useFeatureFlag = (
  flagName: FeatureFlagName
): { value: boolean; isLoading: boolean } => {
  const { flags, isLoading } = useFeatureFlags();
  return { value: flags[flagName] ?? false, isLoading };
};
